import { type ReactNode } from 'react';

import { DEFAULT_PAGINATION_PAGE_SIZE } from '@/consts/table';
import { ColumnPicker } from '@/features/table/components/ColumnPicker';
import type { SelectionMode } from '@/features/table/components/DataGridTree';
import { denseTheme } from '@/features/table/consts/config';
import { useColumnVisibility } from '@/features/table/hooks/useColumnVisibility';
import { cn } from '@/utils/cn';
import type { ColDef } from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import LoadingOverlay from './LoadingOverlay';
import { NoRowsOverlay } from './NoRowsOverlay';
import styles from '@/features/table/styles/DataGrid.module.css';

interface DataGridProps<T = unknown> {
  columnDefs: AgGridReactProps['columnDefs'];
  rowData: AgGridReactProps['rowData'];
  onGridReady?: AgGridReactProps['onGridReady'];
  domLayout?: AgGridReactProps['domLayout'];
  pagination?: AgGridReactProps['pagination'];
  paginationPageSize?: AgGridReactProps['paginationPageSize'];
  loading?: AgGridReactProps['loading'];
  height?: number | string;
  width?: number | string;
  defaultColDef?: AgGridReactProps['defaultColDef'];
  groupDisplayType?: AgGridReactProps['groupDisplayType'];
  rowGroupPanelShow?: AgGridReactProps['rowGroupPanelShow'];
  autoGroupColumnDef?: AgGridReactProps['autoGroupColumnDef'];
  groupDefaultExpanded?: AgGridReactProps['groupDefaultExpanded'];
  initialGroupOrderComparator?: AgGridReactProps['initialGroupOrderComparator'];
  showFooter?: boolean;
  context?: AgGridReactProps<T>['context'];
  showCheckboxSelection?: boolean;
  selectionMode?: SelectionMode;
  selectionColumnDef?: AgGridReactProps<T>['selectionColumnDef'];
  getRowId?: AgGridReactProps<T>['getRowId'];
  onSelectionChanged?: AgGridReactProps<T>['onSelectionChanged'];
  className?: string;
  showColumnPicker?: boolean;
  toolbarActions?: ReactNode;
}

export const DataGrid = <T extends Record<string, unknown>>(props: DataGridProps<T>) => {
  const {
    columnDefs,
    rowData,
    onGridReady,
    domLayout = 'autoHeight',
    pagination = false,
    paginationPageSize = DEFAULT_PAGINATION_PAGE_SIZE,
    loading = false,
    height = '100%',
    width = '100%',
    defaultColDef,
    groupDisplayType,
    rowGroupPanelShow,
    autoGroupColumnDef,
    groupDefaultExpanded,
    initialGroupOrderComparator,
    showFooter = true,
    showCheckboxSelection = false,
    selectionMode = 'multiRow',
    selectionColumnDef,
    getRowId,
    onSelectionChanged,
    className = '',
    showColumnPicker = true,
    toolbarActions
  } = props;

  const { t } = useTranslation();
  const totalRecords = Array.isArray(rowData) ? rowData.length : 0;

  const { visibleColumnDefs, allColumns, visibleColumnIds, handleVisibilityChange } = useColumnVisibility<T>({
    columnDefs: columnDefs as ColDef<T>[]
  });

  const showToolbar = showColumnPicker || toolbarActions !== undefined;

  return (
    <div className={cn(styles.dataGridContainer ?? '', className)} style={{ height, width }}>
      {showToolbar && (
        <div className={styles.treeGridToolbar}>
          {showColumnPicker && (
            <ColumnPicker
              columns={allColumns}
              visibleColumns={visibleColumnIds}
              onVisibilityChange={handleVisibilityChange}
              data-testid="data-grid-column-picker"
            />
          )}
          {toolbarActions}
        </div>
      )}
      <AgGridReact
        theme={denseTheme}
        columnDefs={visibleColumnDefs}
        rowData={rowData}
        defaultColDef={defaultColDef}
        onGridReady={onGridReady}
        domLayout={domLayout}
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        loading={loading}
        loadingOverlayComponent={LoadingOverlay}
        noRowsOverlayComponent={NoRowsOverlay}
        groupDisplayType={groupDisplayType}
        rowGroupPanelShow={rowGroupPanelShow}
        autoGroupColumnDef={autoGroupColumnDef}
        groupDefaultExpanded={groupDefaultExpanded}
        initialGroupOrderComparator={initialGroupOrderComparator}
        tooltipShowMode="whenTruncated"
        singleClickEdit={true}
        stopEditingWhenCellsLoseFocus={true}
        rowSelection={
          showCheckboxSelection
            ? {
                mode: selectionMode,
                checkboxes: true,
                enableClickSelection: false,
                ...(selectionMode === 'multiRow' && { headerCheckbox: true })
              }
            : undefined
        }
        {...(props.context !== undefined && { context: props.context })}
        {...(getRowId !== undefined && { getRowId })}
        {...(onSelectionChanged !== undefined && { onSelectionChanged })}
        selectionColumnDef={showCheckboxSelection ? selectionColumnDef : undefined}
      />
      {showFooter && (
        <div className={styles.gridFooter}>
          <span className={styles.recordCount}>{t('grid.footer.totalRecords', { count: totalRecords })}</span>
        </div>
      )}
    </div>
  );
};
